import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Hook for a digits-only page number input.
 * Keeps local string state so the user can clear the field,
 * and only notifies the parent when the value is valid and has actually changed.
 * Debouncing is handled by the parent sync layer (useTimelineSyncManager).
 */
export function usePageNumberInput(
  propValue: number,
  onChange?: (value: number) => void,
  resetKey?: number,
) {
  const [localValue, setLocalValue] = useState(String(propValue));
  const prevPropRef = useRef(propValue);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Sync from prop when it changes externally (e.g., discard/reset)
  useEffect(() => {
    if (prevPropRef.current !== propValue) {
      setLocalValue(String(propValue));
      prevPropRef.current = propValue;
    }
  }, [propValue]);

  // Re-sync from prop when resetKey changes (e.g., item reset button)
  const prevResetKeyRef = useRef(resetKey);
  useEffect(() => {
    if (prevResetKeyRef.current !== resetKey) {
      setLocalValue(String(propValue));
      prevResetKeyRef.current = resetKey;
    }
  }, [resetKey, propValue]);

  const handleChange = useCallback((value: string) => {
    const digitsOnly = value.replace(/\D/g, "");
    setLocalValue(digitsOnly);
    const num = parseInt(digitsOnly, 10);
    if (!isNaN(num) && num > 0 && num !== prevPropRef.current) {
      onChangeRef.current?.(num);
      prevPropRef.current = num;
    }
  }, []);

  const isError = localValue.trim() === "";

  return { localValue, handleChange, isError };
}
